import { BaseApi } from './base';

class LessonRoomApi extends BaseApi {
  constructor() {
    super('lesson-room');
  }

  /**
   * Get room data including webinar details, lesson info, and current state
   * @param {string|number} webinarId - The webinar ID
   * @returns {Promise} API response with room data
   */
  getRoomData(webinarId) {
    return this.get(`/${webinarId}`);
  }

  /**
   * Join the lesson room
   * @param {string|number} webinarId - The webinar ID
   * @returns {Promise} API response
   */
  joinRoom(webinarId) {
    return this.post(`/${webinarId}/join`);
  }

  /**
   * Leave the lesson room
   * @param {string|number} webinarId - The webinar ID
   * @returns {Promise} API response
   */
  leaveRoom(webinarId) {
    return this.post(`/${webinarId}/leave`);
  }

  /**
   * Start the conference (teacher only)
   * @param {string|number} webinarId - The webinar ID
   * @returns {Promise} API response
   */
  startConference(webinarId) {
    return this.post(`/${webinarId}/conference/start`);
  }

  /**
   * Stop the conference (teacher only)
   * @param {string|number} webinarId - The webinar ID
   * @returns {Promise} API response
   */
  stopConference(webinarId) {
    return this.post(`/${webinarId}/conference/stop`);
  }

  /**
   * Finish the lesson (teacher only) - marks as completed and stops conference
   * @param {string|number} webinarId - The webinar ID
   * @returns {Promise} API response
   */
  finishLesson(webinarId) {
    return this.post(`/${webinarId}/finish`);
  }

  /**
   * Upload a presentation file (teacher only)
   * @param {FormData} formData - Form data containing the presentation file and webinar_id
   * @param {Object} options - Upload options
   * @param {Function} [options.onProgress] - Progress callback function
   * @returns {Promise} API response with uploaded presentation data
   */
  uploadPresentation(formData, options = {}) {
    const webinarId = formData.get('webinar_id');
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    };

    // Add progress tracking if callback provided
    if (options.onProgress) {
      config.onUploadProgress = options.onProgress;
    }

    return this.post(`/${webinarId}/presentation/upload`, formData, config);
  }

  /**
   * Remove current presentation (teacher only)
   * @param {string|number} webinarId - The webinar ID
   * @returns {Promise} API response
   */
  removePresentation(webinarId) {
    return this.delete(`/${webinarId}/presentation`);
  }

  /**
   * Change the current presentation page (teacher only)
   * @param {string|number} webinarId - The webinar ID
   * @param {Object} data - Page change data
   * @param {number} data.page - The page number to go to
   * @param {string|number} [data.presentation_id] - Optional presentation ID if changing presentation
   * @returns {Promise} API response
   */
  changePage(webinarId, data) {
    return this.post(`/${webinarId}/presentation/change-page`, data);
  }

  /**
   * Send a chat message
   * @param {string|number} webinarId - The webinar ID
   * @param {Object} data - Message data
   * @param {string} data.message - The message text
   * @returns {Promise} API response
   */
  sendMessage(webinarId, data) {
    // This will be implemented when we create the chat controller
    // For now, we'll use a placeholder endpoint that follows RESTful patterns
    return this.post(`/${webinarId}/messages`, data);
  }

  /**
   * Get chat messages for the lesson room
   * @param {string|number} webinarId - The webinar ID
   * @param {Object} options - Optional query parameters
   * @param {number} [options.limit] - Maximum number of messages to retrieve
   * @param {number} [options.offset] - Offset for pagination
   * @returns {Promise} API response with messages
   */
  getChatMessages(webinarId, options = {}) {
    const queryParams = new URLSearchParams(options).toString();
    const url = `/${webinarId}/messages${queryParams ? `?${queryParams}` : ''}`;
    return this.get(url);
  }

  /**
   * Get a specific presentation by ID
   * @param {string|number} presentationId - The presentation ID
   * @returns {Promise} API response with presentation data
   */
  getPresentation(presentationId) {
    // This endpoint will be created when we implement presentation management
    return this.get(`/presentations/${presentationId}`);
  }

  /**
   * Delete a presentation (teacher only)
   * @param {string|number} webinarId - The webinar ID
   * @param {string|number} presentationId - The presentation ID
   * @returns {Promise} API response
   */
  deletePresentation(webinarId, presentationId) {
    return this.delete(`/${webinarId}/presentation/${presentationId}`);
  }

  /**
   * Get active participants in the lesson room
   * @param {string|number} webinarId - The webinar ID
   * @returns {Promise} API response with participants list
   */
  getParticipants(webinarId) {
    return this.get(`/${webinarId}/participants`);
  }

  /**
   * Update participant status (for media controls)
   * @param {string|number} webinarId - The webinar ID
   * @param {Object} data - Participant status data
   * @param {boolean} [data.hasAudio] - Whether participant has audio enabled
   * @param {boolean} [data.hasVideo] - Whether participant has video enabled
   * @returns {Promise} API response
   */
  updateParticipantStatus(webinarId, data) {
    return this.post(`/${webinarId}/participant/status`, data);
  }

  /**
   * Get lesson room statistics (for teachers)
   * @param {string|number} webinarId - The webinar ID
   * @returns {Promise} API response with room statistics
   */
  getRoomStatistics(webinarId) {
    return this.get(`/${webinarId}/statistics`);
  }

  // ===== QUIZ MANAGEMENT METHODS =====

  /**
   * Get available test packs for quiz creation (teacher only)
   * @param {string|number} webinarId - The webinar ID
   * @returns {Promise} API response with test packs
   */
  getAvailableTestPacks(webinarId) {
    return this.get(`/${webinarId}/quiz/test-packs`);
  }

  /**
   * Get the active quiz for the lesson room
   * @param {string|number} webinarId - The webinar ID
   * @returns {Promise} API response with active quiz data
   */
  getActiveQuiz(webinarId) {
    return this.get(`/${webinarId}/quiz/active`);
  }

  /**
   * Create a new quiz (teacher only)
   * @param {string|number} webinarId - The webinar ID
   * @param {Object} data - Quiz creation data
   * @param {string|number} data.testPackId - Test pack ID to create quiz from
   * @param {string} data.title - Quiz title
   * @param {boolean} [data.showResultsImmediately] - Whether to show results immediately
   * @param {boolean} [data.randomizeQuestions] - Whether to randomize question order
   * @param {number} [data.timeLimitMinutes] - Time limit in minutes
   * @param {number} [data.attemptsAllowed] - Number of attempts allowed
   * @returns {Promise} API response with created quiz data
   */
  createQuiz(webinarId, data) {
    // Transform camelCase to snake_case for backend
    const transformedData = {
      test_pack_id: data.testPackId,
      title: data.title,
      instructions: data.instructions,
      time_limit: data.timeLimitMinutes,
      show_results_immediately: data.showResultsImmediately,
      allow_review: data.allowReview || false
    };

    return this.post(`/${webinarId}/quiz`, transformedData);
  }

  /**
   * Start a quiz (teacher only)
   * @param {string|number} webinarId - The webinar ID
   * @param {string|number} quizId - The quiz ID
   * @returns {Promise} API response
   */
  startQuiz(webinarId, quizId) {
    return this.post(`/${webinarId}/quiz/${quizId}/start`);
  }

  /**
   * Get quiz data for student to take
   * @param {string|number} webinarId - The webinar ID
   * @param {string|number} quizId - The quiz ID
   * @returns {Promise} API response with quiz questions and user submission
   */
  getQuizForStudent(webinarId, quizId) {
    return this.get(`/${webinarId}/quiz/${quizId}/student`);
  }

  /**
   * Save quiz answers (auto-save during quiz taking)
   * @param {string|number} webinarId - The webinar ID
   * @param {string|number} quizId - The quiz ID
   * @param {Object} data - Answer data
   * @param {Object} data.answers - User answers object (questionId -> answerIds array)
   * @returns {Promise} API response
   */
  saveQuizAnswers(webinarId, quizId, data) {
    return this.post(`/${webinarId}/quiz/${quizId}/save-answers`, data);
  }

  /**
   * Submit quiz (student only)
   * @param {string|number} webinarId - The webinar ID
   * @param {string|number} quizId - The quiz ID
   * @param {Object} data - Submission data
   * @param {Object} data.answers - User answers object (questionId -> answerIds array)
   * @returns {Promise} API response with submission results
   */
  submitQuiz(webinarId, quizId, data) {
    return this.post(`/${webinarId}/quiz/${quizId}/submit`, data);
  }

  /**
   * End quiz (teacher only)
   * @param {string|number} webinarId - The webinar ID
   * @param {string|number} quizId - The quiz ID
   * @returns {Promise} API response
   */
  endQuiz(webinarId, quizId) {
    return this.post(`/${webinarId}/quiz/${quizId}/end`);
  }

  /**
   * Get quiz results and analytics (teacher only)
   * @param {string|number} webinarId - The webinar ID
   * @param {string|number} quizId - The quiz ID
   * @returns {Promise} API response with detailed quiz results
   */
  getQuizResults(webinarId, quizId) {
    return this.get(`/${webinarId}/quiz/${quizId}/results`);
  }

  /**
   * Get user's quiz submission (for ended quizzes)
   * @param {string|number} webinarId - The webinar ID
   * @param {string|number} quizId - The quiz ID
   * @returns {Promise} API response with user submission
   */
  getUserQuizSubmission(webinarId, quizId) {
    return this.get(`/${webinarId}/quiz/${quizId}/submission`);
  }

  /**
   * Export quiz results in specified format (teacher only)
   * @param {string|number} webinarId - The webinar ID
   * @param {string|number} quizId - The quiz ID
   * @param {string} format - Export format (csv, excel, pdf)
   * @returns {Promise} API response with file data
   */
  exportQuizResults(webinarId, quizId, format) {
    return this.get(`/${webinarId}/quiz/${quizId}/export/${format}`, {
      responseType: 'blob'
    });
  }
}

export default new LessonRoomApi();